import React, { useMemo, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';

import VideoPreview from './VideoPreview.js';
import './VideoOutputCard.scss';

const ACTIVE_PROGRESS_MAX = 100;

const joinClassNames = (...values) => values.filter(Boolean).join(' ');

const formatTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleString('zh-CN', { hour12: false });
};

const VideoOutputCard = ({
  className = '',
  errorMessage = '',
  isFailed = false,
  isSuccess = false,
  name,
  onActionError,
  onChanged,
  onDownload,
  onRetry,
  onSelect,
  progress = 0,
  selected = false,
  showMeta = true,
  statusLabel,
  statusTone = 'muted',
  time,
  title,
  videoUrl,
}) => {
  const [busyAction, setBusyAction] = useState('');
  const normalizedProgress = useMemo(
    () => Math.max(0, Math.min(ACTIVE_PROGRESS_MAX, Number(progress) || 0)),
    [progress],
  );
  const hasActions = (
    (isSuccess && typeof onSelect === 'function')
    || (isSuccess && typeof onDownload === 'function')
    || (isFailed && typeof onRetry === 'function')
  );

  const runAction = async (action, operation, fallback) => {
    if (busyAction || typeof operation !== 'function') return;
    setBusyAction(action);
    try {
      const result = await operation();
      onChanged?.(result, action);
    } catch (error) {
      onActionError?.(error, fallback, action);
    } finally {
      setBusyAction('');
    }
  };

  return (
    <article className={joinClassNames('video-output-card', className)}>
      {isSuccess && videoUrl ? (
        <VideoPreview
          ariaLabel={`播放 ${name || title || '成片'}`}
          className="video-output-card__preview"
          overlay={(
            <span className="video-output-card__play" aria-hidden="true">
              <i className="bi bi-play-fill" />
            </span>
          )}
          src={videoUrl}
          title={name || title || '成片'}
        />
      ) : (
        <div className={joinClassNames(
          'video-output-card__state',
          isFailed && 'is-failed',
        )}>
          <i className={`bi ${isFailed ? 'bi-exclamation-triangle' : 'bi-film'}`} />
          <strong>{statusLabel || '等待处理'}</strong>
          {isFailed && errorMessage ? (
            <p className="video-output-card__error" title={errorMessage}>
              {errorMessage}
            </p>
          ) : null}
          {!isFailed ? (
            <div
              className="video-output-card__progress"
              aria-label={`处理进度 ${normalizedProgress}%`}
            >
              <span style={{ width: `${normalizedProgress}%` }} />
            </div>
          ) : null}
        </div>
      )}

      {showMeta ? (
        <div className="video-output-card__body">
          <div className="video-output-card__heading">
            <strong title={title || ''}>{title || '视频成片'}</strong>
            <span className={`video-output-card__status is-${statusTone}`}>
              {statusLabel || '-'}
            </span>
          </div>
          <div className="video-output-card__name" title={name || ''}>
            {name || '-'}
          </div>
          <time>{formatTime(time)}</time>
        </div>
      ) : null}

      {hasActions ? <div className="video-output-card__actions">
        {isSuccess && typeof onSelect === 'function' ? (
          <Button
            size="sm"
            variant={selected ? 'success' : 'outline-success'}
            disabled={Boolean(busyAction) || selected}
            onClick={() => runAction('select', onSelect, '选用成片失败')}
          >
            {busyAction === 'select' ? (
              <Spinner animation="border" size="sm" aria-label="正在选用" />
            ) : (
              <i className={`bi ${selected ? 'bi-check2-circle' : 'bi-check2'}`} />
            )}
            <span>{selected ? '已选用' : '选用'}</span>
          </Button>
        ) : null}
        {isSuccess && typeof onDownload === 'function' ? (
          <Button
            size="sm"
            variant="outline-primary"
            disabled={Boolean(busyAction)}
            onClick={() => runAction('download', onDownload, '下载视频失败')}
          >
            {busyAction === 'download' ? (
              <Spinner animation="border" size="sm" aria-label="正在下载" />
            ) : (
              <i className="bi bi-download" />
            )}
            <span>下载</span>
          </Button>
        ) : null}
        {isFailed && typeof onRetry === 'function' ? (
          <Button
            size="sm"
            variant="outline-danger"
            disabled={Boolean(busyAction)}
            onClick={() => runAction('retry', onRetry, '重试任务失败')}
          >
            {busyAction === 'retry' ? (
              <Spinner animation="border" size="sm" aria-label="正在重试" />
            ) : (
              <i className="bi bi-arrow-repeat" />
            )}
            <span>重试</span>
          </Button>
        ) : null}
      </div> : null}
    </article>
  );
};

export default VideoOutputCard;
